import type { IconButtonProps as MuiIconButtonProps } from '@mui/material';
import { IconButton as MuiIconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { forwardRef } from 'react';

import { InlineSpinner } from './InlineSpinner';
import { guardedClick, isAriaDisabled } from './softDisabled';

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'aria-label' | 'color' | 'size'> {
  label: string;
  tooltip?: boolean;
  intent?: 'default' | 'danger';
  size?: 'small' | 'medium';
  loading?: boolean;
}

const IconButtonContent = ({ loading, children }: { loading: boolean; children: React.ReactNode }) => {
  if (loading) return <InlineSpinner />;
  return <>{children}</>;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tooltip = true, intent = 'default', size = 'small', loading = false, disabled, children, sx, onClick, 'aria-disabled': ariaDisabled, ...rest },
  ref,
) {
  const theme = useTheme();
  const tooltipTitle = tooltip && !disabled && !loading ? label : '';
  const unavailable = loading || isAriaDisabled(ariaDisabled);
  return (
    <Tooltip title={tooltipTitle} disableInteractive>
      <MuiIconButton
        ref={ref}
        aria-label={label}
        size={size}
        color={intent === 'danger' ? 'error' : 'default'}
        disabled={disabled}
        aria-disabled={loading || ariaDisabled}
        aria-busy={loading || undefined}
        onClick={guardedClick(unavailable, onClick)}
        sx={[
          {
            width: theme.controlHeight[size],
            height: theme.controlHeight[size],
            '@media (pointer: coarse)': { width: theme.controlHeight.medium, height: theme.controlHeight.medium },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
          { '&[aria-disabled="true"]': { color: theme.palette.action.disabled, cursor: 'not-allowed' } },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        <IconButtonContent loading={loading}>{children}</IconButtonContent>
      </MuiIconButton>
    </Tooltip>
  );
});
